import React, {Fragment} from 'react';

import cs from 'classnames';
import {PopoverBody} from 'reactstrap';
import styled from 'styled-components';

import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {PopoverHeading} from 'app/general/overlays';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import StateButton from 'app/general/widgets/StateButton';
import {gt} from 'app/utils/i18n';

import {FullHTMLLink} from '../FullHTMLLink';
import {LazyTooltip} from '../LazyTooltip';
import NoResultsMessage from '../NoResultsMessage';

interface CommonItemType {
  name: string;
  url: string;
  html_url: string;
}

interface Props<T> {
  topHeading: string;
  bottomHeading: string;
  noResultsHeading: string;
  noResultsIcon: string;
  loadingText: string;
  attachedItems?: Array<T>;
  unAttachedItems?: Array<T>;
  search: string;
  removeTooltipText?: string;
  addTooltipText?: string;
  onSearch: (search: string) => unknown;
  onAdd: (item: T) => void;
  onRemove: (item: T) => void;
  onClose: () => void;
}

export function GenericPopover<T extends CommonItemType>({
  topHeading,
  bottomHeading,
  noResultsHeading,
  noResultsIcon,
  loadingText,
  attachedItems,
  unAttachedItems,
  search,
  removeTooltipText = 'Remove',
  addTooltipText = 'Add',
  onSearch,
  onAdd,
  onRemove,
  onClose,
}: Props<T>) {
  return (
    <Fragment>
      <PopoverBody className="m-0 p-0 ">
        <StyledScrollableContainerDiv className="d-flex flex-column">
          {attachedItems != null && attachedItems.length > 0 ? (
            <Fragment>
              <PopoverHeading key="popoverheading" className="popover-header">
                {gt(topHeading)}
              </PopoverHeading>
              <ul
                className="list-group list-group-flush flex-grow-1 flex-shrink-0 overflow-auto"
                style={{
                  maxHeight: 'calc(60vh - 230px)',
                }}
              >
                {attachedItems.map((item) => (
                  <li
                    key={item.url}
                    style={{
                      borderColor: 'rgba(255,255,255,0.5)',
                    }}
                    className={cs(
                      'active list-group-item py-1 d-flex align-items-center ' +
                        'justify-content-between pr-0'
                    )}
                  >
                    <FullHTMLLink
                      html_url={item.html_url}
                      className="text-white text-truncate py-2"
                    >
                      {item.name}
                    </FullHTMLLink>
                    <LazyTooltip
                      placement="left"
                      tooltip={gt(removeTooltipText)}
                    >
                      <StateButton
                        className="btn-sm btn-link py-1 px-3 text-white"
                        onClick={() => onRemove(item)}
                      >
                        <i className="fa fa-fw fa-times" />
                      </StateButton>
                    </LazyTooltip>
                  </li>
                ))}
              </ul>
            </Fragment>
          ) : null}
          <PopoverHeading>{gt(bottomHeading)}</PopoverHeading>
          <SearchInputField
            autoFocus
            value={search}
            onChange={(q) => onSearch(q)}
            placeholder={gt('Search')}
          />
          {unAttachedItems == null ? (
            <p className="text-center py-3 px-3 mb-0">
              <i className="fa fa-circle-notch fa-spin mr-2" />
              {gt(loadingText)}...
            </p>
          ) : (
            <div className="overflow-auto">
              {unAttachedItems !== null && unAttachedItems.length > 0 ? (
                <Fragment>
                  {unAttachedItems.map((item) => (
                    <li
                      key={item.url}
                      className={cs(
                        'list-group-item py-1 d-flex align-items-center ' +
                          'justify-content-between pr-0'
                      )}
                    >
                      <FullHTMLLink
                        html_url={item.html_url}
                        className="text-truncate py-2"
                      >
                        {item.name}
                      </FullHTMLLink>
                      <LazyTooltip
                        placement="left"
                        tooltip={gt(addTooltipText)}
                      >
                        <StateButton
                          className="btn-sm btn-link py-1 px-3"
                          onClick={() => onAdd(item)}
                        >
                          <i className="fas fa-plus" />
                        </StateButton>
                      </LazyTooltip>
                    </li>
                  ))}
                </Fragment>
              ) : (
                <NoResultsMessage
                  onSidebar={false}
                  onPopover={true}
                  icon={noResultsIcon}
                  head={gt(noResultsHeading)}
                  classNameOverwrite="my-4 text-center"
                />
              )}{' '}
            </div>
          )}
        </StyledScrollableContainerDiv>
      </PopoverBody>
      <StyledPopoverBottomDIV>
        <button onClick={onClose} className="btn btn-link btn-sm">
          <i className="fa fa-times" /> {gt('Close')}
        </button>
      </StyledPopoverBottomDIV>
    </Fragment>
  );
}

const StyledScrollableContainerDiv = styled.div`
  max-height: calc(100vh - 200px);
  width: 274px;
`;
